// Order Management module internationalization - English
import formEngine from './order-form-engine'
import clientDetail from './order-client-detail'

export default {
  title: 'Order Management',

  // Fields
  orderNo: 'Order Number',
  customerName: 'Customer Name',
  orderAmount: 'Order Amount',
  transportType: 'Transport Type',
  origin: 'Origin',
  destination: 'Destination',
  estimatedDelivery: 'Estimated Delivery',
  amountRange: 'Amount Range',
  createDateRange: 'Creation Date',
  orderType: 'Order Type',
  quoteNo: 'Quote Number',
  cargoJobNo: 'Cargo Job No.',
  customReferenceNo: 'Reference No.',
  mblNo: 'MBL No.',
  hblNo: 'HBL No.',
  containerNo: 'Container No.',

  // Status
  status: {
    pending: 'Pending',
    confirmed: 'Confirmed',
    inTransit: 'In Transit',
    completed: 'Completed',
    cancelled: 'Cancelled'
  },

  // Transport Types
  transportTypes: {
    sea: 'Sea Freight',
    air: 'Air Freight',
    rail: 'Rail Freight',
    road: 'Road Freight'
  },

  // Order Types
  types: {
    needQuote: 'Need Quote',
    hasQuote: 'Has Quote',
    booking: 'Booking Service'
  },

  // Actions
  actions: {
    createOrder: 'Create Order',
    placeOrder: 'Place Order',
    exportData: 'Export Data',
    customizeTable: 'Customize Table',
    syncOrder: 'Sync Order',
    triggerSync: 'Trigger Sync for Selected Orders',
    view: 'View',
    delete: 'Delete',
    editDraft: 'Continue editing'
  },

  unifiedCreate: {
    clientTitle: 'Entrusted Order',
    adminTitle: 'Order on Behalf',
    editDraftTitle: 'Edit draft',
    subtitle: 'Form engine consumer',
    businessTypeLabel: 'Business type:',
    formTitle: 'Form · {name}',
    templateReady: 'Template ready',
    templateMissing: 'Template pending',
    noTemplateHint: 'No published form template exists for this business type yet.',
    saveDraft: 'Save draft',
    submitOrder: 'Submit order',
    draftSaved: 'Draft saved',
    submitSuccess: 'Order submitted',
    validationFailed: 'Validation failed. Please check highlighted fields.',
    validationRequiredFailed: 'Please fill in all required fields (highlighted in red).',
    validationRequestFailed: 'Validation request failed. Please try again later.',
    echoLoadFailed: 'Failed to load order data. Please refresh and try again.',
    selectBusinessHint: 'Select a business type above to start',
    attachmentTitle: 'Attachments',
    attachmentTip: 'PDF/JPG/PNG/DOC supported, up to 10 files',
    sourceBannerText: 'Data imported from "{label}" · {keyLabel} {keyValue}',
    clearSource: 'Clear Source',
    sourceCleared: 'Source data cleared. You may switch business type or fill manually.',
    retry: 'Retry',
    manualFill: 'Manual Fill',
    switchedToManual: 'Switched to manual entry.',
    bizLockedHint: 'Data imported from source. Clear source before switching business type.',
    notIntegratedHint: 'Source data for "{biz}" is not integrated yet. Please fill manually.',
    estimatedTotalFee: 'Estimated Total',
    mixedCurrency: 'Mixed Currencies',
    viewFeeDetail: 'Details'
  },
  sourceAdapter: {
    rateQuery: 'Rate Query',
    quoteRecord: 'Quote Record',
    inquiry: 'Inquiry',
    quotation: 'Quotation',
    specialPrice: 'Special Price',
    shareQuote: 'Shared Quote',
    rateNo: 'Rate No.',
    inquiryNo: 'Inquiry No.'
  },

  // Service items (entrusted order form)
  serviceItems: {
    fieldLabel: 'Service Items',
    sectionTitle: 'Service Items',
    placeholder: 'Please select service items',
    BOOKING_SERVICE: 'Booking',
    TRUCKING_SERVICE: 'Trucking',
    STUFFING_SERVICE: 'Container Stuffing',
    INSURANCE_SERVICE: 'Cargo Insurance',
    CUSTOMS_SERVICE: 'Customs Declaration',
    DESTINATION_EXCHANGE_ORDER_SERVICE: 'Destination D/O Exchange',
    DESTINATION_WAREHOUSING_SERVICE: 'Destination Warehousing',
    DESTINATION_DELIVERY_SERVICE: 'Destination Delivery',
    DESTINATION_CUSTOMS_CLEARANCE_SERVICE: 'Destination Customs Clearance'
  },

  formEngine,
  clientDetail,

  businessType: {
    FCL: 'FCL Full Container Load',
    LCL: 'LCL Less than Container Load',
    AIR: 'AIR Air Freight',
    RAIL: 'RAIL Rail Freight',
    TRUCK: 'TRUCK Road Freight',
    LTL: 'LTL Less-than-Truckload',
    TRAILER: 'TRAILER Trailer',
    RORO: 'RORO Roll-on/Roll-off',
    BULK: 'BULK Bulk Cargo'
  },

  // Short labels for the order-detail header / business-type field
  businessTypeShort: {
    FCL: 'Sea FCL',
    LCL: 'Sea LCL',
    AIR: 'Air',
    RAIL: 'Rail',
    TRUCK: 'Road',
    LTL: 'LTL',
    TRAILER: 'Trailer',
    RORO: 'RoRo',
    BULK: 'Bulk'
  },

  businessFieldsDrawer: {
    title: 'Business Fields · {orderNo}',
    businessType: 'Business Type',
    viewAction: 'View Fields',
    viewJson: 'View JSON',
    hideJson: 'Hide JSON',
    empty: 'No business fields'
  },

  // Export
  export: {
    mode: 'Export Mode',
    modeSelected: 'Selected Records',
    modeFiltered: 'All Filtered Records',
    modeCurrentPage: 'Current Page Records',
    successTitle: 'Export Task Submitted',
    jumpPrompt: 'The task is being processed. Would you like to go to the Task Center to check the progress?',
    goToTaskManager: 'Go to Task Center',
    taskManagerRedirectTitle: 'Task Center',
    jumpCancel: 'Stay on Page',
    csvSuccess: 'CSV export successful',
    excelSuccess: 'Excel export successful',
    csvFailed: 'CSV export failed',
    excelFailed: 'Excel export failed',
    exportSuccess: 'Export successful',
    noDataToExport: 'No data to export',
    filename: 'Orders_Export_Data',
    sheetTitle: 'Orders Sheet Data'
  },

  // Forms
  forms: {
    orderNoPlaceholder: 'Please enter order number',
    statusPlaceholder: 'Please select status',
    businessTypePlaceholder: 'Please select business type',
    transportTypePlaceholder: 'Please select transport type',
    customerNamePlaceholder: 'Please enter customer name',
    minAmount: 'Min Amount',
    maxAmount: 'Max Amount',
    orderTypeRequired: 'Please select order type',
    quoteNoRequired: 'Please enter quote number',
    quoteNoPlaceholder: 'Please enter quote number',
    cargoJobNoPlaceholder: 'Please enter Cargo Job No.',
    customReferenceNoPlaceholder: 'Please enter Reference No.',
    mblNoPlaceholder: 'Please enter MBL No.',
    hblNoPlaceholder: 'Please enter HBL No.',
    containerNoPlaceholder: 'Please enter Container No.',
    polPlaceholder: 'Please select port of loading',
    podPlaceholder: 'Please select port of discharge',
    podSeaPlaceholder: 'Please select port of discharge',
    carrierPlaceholder: 'Please select carrier',
    vesselVoyagePlaceholder: 'Please enter vessel/voyage',
    orderStatusPlaceholder: 'Please select order status',
    orderTypePlaceholder: 'Please select order type',
    etdPlaceholder: 'Please select ETD range',
    etaPlaceholder: 'Please select ETA range',
    laneCodePlaceholder: 'Please select lane code',
    cutoffTimePlaceholder: 'Please select cutoff time range',
    transitPortsPlaceholder: 'Please select transit port',
    bookingNoPlaceholder: 'Please enter booking number',
    amsNoPlaceholder: 'Please enter AMS No',
    isfNoPlaceholder: 'Please enter ISF No',
    orderType: 'Order Type',
    clientId: 'Client',
    clientIdPlaceholder: 'Please select client',
    clientIdRequired: 'Please select client',
    quoteNoLabel: 'Quote Number',
    quoteNoInputPlaceholder: 'Please enter quote number'
  },

  // AI Assistant
  aiAssistant: {
    title: 'AI Order Assistant',
    welcomeMessage: 'Hello! I am the AI order management assistant. I can help you query order status, analyze shipping trends, optimize logistics solutions, etc. How can I help you?',
    inputPlaceholder: 'Please enter your question...',
    send: 'Send',
    mockResponse: 'You asked "{question}", which is a great question. AI functionality is under development, please stay tuned!'
  },

  // Enums
  enum: {
    transportMode: {
      FCL: 'Full Container Load',
      LCL: 'Less than Container Load'
    },
    status: {
      draft: 'Draft',
      inProgress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled'
    },
    type: {
      sea: 'Sea Freight Order',
      air: 'Air Freight Order',
      land: 'Land Freight Order',
      rail: 'Rail Freight Order'
    },
    createType: {
      needQuote: 'Need Quote',
      hasQuote: 'Has Quote',
      booking: 'Booking Service'
    },
    // Order source: CW = synced in from CargoWare, CWX = created in CargoWareX
    orderSource: {
      cw: 'CargoWare',
      cwx: 'CargoWareX',
      cwTip: 'Created in CargoWare and synced in; edit back in CargoWare'
    },
    nodeTaskStatus: {
      incomplete: 'Incomplete',
      partial: 'Partially Completed',
      completed: 'Completed'
    },
    // SOP business node state type, values aligned with CargoWare stateType (CCTOWER-3288)
    sopStateType: {
      notStarted: 'Not Started',
      inProgress: 'In Progress',
      completed: 'Completed',
      exception: 'Exception'
    },
    taskStatus: {
      notStarted: 'Not Started',
      inProgress: 'In Progress',
      completed: 'Completed',
      skipped: 'Skipped',
      paused: 'Paused'
    },
    taskType: {
      customer: 'Customer Task',
      operation: 'Operation Task'
    },
    priority: {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      urgent: 'Urgent'
    },
    partyType: {
      shipper: 'Shipper',
      consignee: 'Consignee',
      notify: 'Notify Party',
      customsShipper: 'Customs Shipper Address',
      customsConsignee: 'Customs Consignee Address',
      clearanceShipper: 'Clearance Shipper Address',
      clearanceConsignee: 'Clearance Consignee Address',
      actualPickup: 'Actual Pickup Address',
      actualDelivery: 'Actual Delivery Address',
      notify2: 'Notify Party 2'
    },
    chassisAbnormalStatus: {
      all: 'All',
      hasAbnormal: 'Yes',
      noAbnormal: 'No'
    },
    syncStatus: {
      notSynced: 'Not Synced',
      syncing: 'Syncing',
      synced: 'Synced',
      syncFailed: 'Sync Failed',
      preReviewRejected: 'Pre-Review Rejected'
    },
    cargoType: {
      general: 'General Cargo',
      dangerous: 'Dangerous Goods',
      reefer: 'Frozen Goods',
      special: 'Special Container',
      steelCoil: 'Coiled Steel',
      liquid: 'Liquid',
      chemical: 'Chemical',
      textile: 'Textile',
      reeferDangerous: 'Hazardous Cold Materials'
    },
    releaseType: {
      original: 'Original',
      telex: 'Telex Release',
      switch: 'Switch B/L',
      seawaybill: 'Sea Waybill',
      destinationRelease: 'Destination Release',
      pending: 'Pending',
      notifyRelease: 'Notify Release',
      ebl: 'E-B/L'
    },
    orderFileType: {
      draftBillOfLading: 'Draft bill of lading',
      originalBillOfLading: 'Original bill of lading',
      telexReleaseGuarantee: 'Telex release guarantee',
      customsDeclaration: 'Customs declaration',
      draftCustomsDeclaration: 'Draft customs declaration',
      releaseNotice: 'Release notice',
      customsDeclarationCopy: 'Customs declaration copy',
      preTaxDocument: 'Pre-tax document',
      officialTaxDocument: 'Official tax document',
      cargoRelated: 'Cargo related file',
      certificationReport: 'Certification report',
      supplierUpload: 'Supplier uploaded file',
      sixCopyForm: 'Six-copy Form',
      customsTransitAdvanceReleaseForm: 'Advance Release Form for Customs Transit',
      customsTransitAdvanceDeclarationForm: 'Advance Declaration Form for Customs Transit'
    },
    spaceStatus: {
      1: 'Smooth',
      2: 'Normal',
      3: 'Single Apply',
      4: 'Overbooked',
      5: 'Not Accepting',
      6: 'Limited',
      7: 'Suspended',
      8: 'Inquiry'
    }
  },

  // Messages
  messages: {
    searchSuccess: 'Search successful',
    searchFailed: 'Search failed',
    createSuccess: 'Order created successfully',
    createFailed: 'Order creation failed',
    editPending: 'Edit order {orderNo} function under development...',
    deletePending: 'Delete order {orderNo} function under development...',
    deleteSuccess: 'Order deleted successfully: {orderNo}',
    deleteFailed: 'Failed to delete order: {orderNo}',
    deleteFailedWithReason: 'Failed to delete order {orderNo}: {reason}',
    confirmDelete: 'Are you sure you want to delete order {orderNo}?',
    onlyDraftCanDelete: 'Only draft status orders can be deleted',
    exportPending: 'Export function under development...',
    customizeTablePending: 'Customize table function under development...',
    loadDataFailed: 'Failed to load order data',
    queryQuoteFailed: 'Failed to query quote record',
    triggerSyncSuccess: 'Successfully triggered sync for {count} orders',
    triggerSyncFailed: 'Failed to trigger sync',
    selectOrdersFirst: 'Please select orders to sync first',
    selectPlatform: 'Please select third-party platform',
    selectSyncMode: 'Please select at least one sync mode',
    syncSuccess: 'Sync successful!',
    syncSuccessTitle: '🎉 Sync successful!',
    newOrders: 'New Orders:',
    updateOrders: 'Updated Orders:',
    tickets: 'orders',
    nodePrefix: 'Node'
  },

  // Table columns
  columns: {
    orderNo: 'Order Number',
    pol: 'Port of Loading',
    pod: 'Port of Discharge',
    podSea: 'Port of Discharge',
    pdl: 'Destination Port',
    carrier: 'Carrier',
    vesselName: 'Vessel/Voyage',
    etd: 'ETD',
    createdBy: 'Created By',
    dateCreated: 'Created Date',
    cargoJobNo: 'Cargo Job No.',
    mblNo: 'MBL No.',
    hblNo: 'HBL No.',
    containerNos: 'Container No.',
    actions: 'Actions',
    syncStatus: 'Sync Status',
    customReferenceNo: 'Reference No.',
    eta: 'ETA',
    laneCode: 'Lane Code',
    terminal: 'Terminal',
    cutoffTime: 'Cutoff Time',
    transitPorts: 'Transit Port',
    voyageDays: 'Transit Time',
    containerDetails: 'Container Type/Qty',
    bookingNo: 'Booking No.',
    amsNo: 'AMS No.',
    isfNo: 'ISF No.',
    shippingTerms: 'Shipping Terms',
    status: 'Order Status',
    orderSource: 'Order Source',
    sourceCode: 'Source No.',
    businessType: 'Business Type',
    businessFields: 'Business Fields'
  },

  // Tasks (top level, for independent task pages)
  tasks: {
    // Bill of Lading Addition Page
    blAddition: {
      title: 'BL Addition - {orderNo}',
      back: 'Back',
      aiRecognition: 'AI Recognition',
      generateGuarantee: 'Generate Guarantee',
      rules: 'Rules',
      saveDraft: 'Save Draft',
      submit: 'Submit',
      guide: {
        title: 'BL Addition Guide',
        tips: {
          title: 'Filling Tips:',
          tip1: 'Shipper and consignee must fill in complete company name and detailed address',
          tip2: 'Goods description must match actual goods and be filled in English',
          tip3: 'Weight and dimensions must be accurately filled in with units indicated',
          tip4: 'Container number format must be correct, e.g., 1*20GP, 2*40HQ, etc.',
          tip5: 'It is recommended to use AI recognition function to quickly import information'
        },
        errors: {
          title: 'Common Errors:',
          error1: 'BL information inconsistent with booking order',
          error2: 'Incomplete shipper/consignee information',
          error3: 'Weight does not match goods description',
          error4: 'Missing or inaccurate dangerous goods information'
        }
      },
      validation: {
        title: 'BL Addition Validation',
        mandatoryRules: 'Mandatory Validation Rules',
        mandatoryRulesDesc: 'The following rules must be met, otherwise the BL may not be processed normally:',
        cancel: 'Cancel',
        confirmSubmit: 'Confirm Submit'
      },
      aiModal: {
        title: 'AI Recognition',
        uploadTitle: 'Upload BL document for AI recognition',
        uploadTip: 'Supports JPG, PNG, PDF formats, file size should not exceed 10MB',
        clickOrDrag: 'Click or drag to upload',
        pdfFormat: 'PDF format',
        recognizing: 'Recognizing...',
        startRecognition: 'Start Recognition',
        resultComparison: 'Recognition Result Comparison',
        acceptAll: 'Accept All',
        rejectAll: 'Reject All',
        cancel: 'Cancel'
      },
      guaranteeModal: {
        title: 'Select Guarantee Type',
        description: 'Please select the guarantee type(s) to generate (multiple selection allowed):',
        cancel: 'Cancel',
        generate: 'Generate Guarantee'
      },
      fields: {
        shipper: 'Shipper',
        consignee: 'Consignee',
        notifyParty: 'Notify Party',
        blType: 'BL Type:',
        original: 'Original',
        surrendered: 'Surrendered',
        seaWaybill: 'SEA WAYBILL',
        blNo: 'B/L NO.',
        blAgent: 'B/L Agent',
        forwardingAgent: 'Forwarding Agent',
        masterBlNo: 'Master B/L NO.',
        placeOfLoading: 'Place of Loading',
        placeOfLoadingPlaceholder: 'Please select place of loading',
        placeOfIssue: 'Place of Issue',
        countryOfOrigin: 'Country of Origin',
        countryOfOriginPlaceholder: 'Please select country of origin',
        remark: 'Remark',
        vessel: 'Vessel',
        voyage: 'Voyage',
        tradeTerms: 'Trade Terms',
        tradeTermsPlaceholder: 'Please select trade terms',
        placeOfReceipt: 'Place of Receipt',
        placeOfReceiptPlaceholder: 'Please select place of receipt',
        pol: 'Port of Loading',
        polPlaceholder: 'Please select port of loading',
        pod: 'Port of Discharge',
        podPlaceholder: 'Please select port of discharge',
        placeOfDelivery: 'Place of Delivery',
        placeOfDeliveryPlaceholder: 'Please select place of delivery',
        hsCode: 'HS Code',
        grossWeight: 'Gross Weight',
        grossWeightUnit: '(Gross Weight)',
        measurement: 'Measurement',
        measurementUnit: '(Measurement)',
        pieces: 'Pieces',
        piecesUnit: '(No.of Pkgs)',
        piecesType: 'Pieces Type',
        piecesTypeUnit: '(Pieces Type)',
        piecesTypePlaceholder: 'Please select pieces type',
        description: 'Description',
        descriptionUnit: '(Description)',
        marks: 'Marks',
        marksUnit: '(Marks)',
        containerNo: 'Container No.',
        containerType: 'Container Type',
        sealNo: 'Seal No.',
        containerWeight: 'Container Weight',
        containerPieces: 'Pieces',
        containerWeightField: 'Weight',
        containerCubage: 'CBM',
        addContainer: 'Add Container',
        removeContainer: 'Remove Container',
        blQuantity: 'Quantity of B/L',
        blWay: 'Way of B/L',
        issueDate: 'Issue Date',
        issueDatePlaceholder: 'Please select issue date',
        portRequirements: 'Port Requirements',
        portRequirementsPlaceholder: 'Packing List Requirements:\nCertificate of Origin:'
      },
      messages: {
        saveDraftFailed: 'Cannot save draft: Task information not found',
        validationFailed: 'Data validation failed:\n{errors}',
        saveDraftSuccess: 'Draft saved successfully',
        uploadFileFirst: 'Please upload file first',
        aiRecognitionComplete: 'AI recognition completed',
        acceptAllResults: 'All recognition results accepted',
        rejectAllResults: 'All recognition results rejected',
        selectGuaranteeType: 'Please select at least one guarantee type',
        guaranteeGenerated: '{count} guarantee(s) generated',
        taskInstanceIdNotExists: 'Task instance ID does not exist',
        submitSuccess: 'BL addition submitted successfully',
        submitFailed: 'Submit failed, please try again',
        getTaskDataFailed: 'Failed to get task data'
      }
    },

    // BC Document Upload Page
    bcUpload: {
      title: 'BC Upload - Order No.: {orderId}',
      pageTitle: 'BC Document Upload',
      back: 'Back',
      uploadTitle: 'BC Document Upload',
      dragText: 'Drag file here or click to upload',
      supportedFormats: 'Supports PDF, JPG, PNG, DOC, DOCX formats',
      fileSizeLimit: 'File size should not exceed 10MB',
      uploadTip: 'Please upload clear BC document, the system will automatically recognize key information',
      verification: {
        title: 'Information Verification',
        filePreview: 'File Preview',
        viewOriginal: 'View Original File',
        recognizedFields: 'Recognized Data Fields',
        systemFields: 'System Existing Fields',
        reupload: 'Re-upload',
        confirm: 'Confirm',
        success: 'BC document processing completed'
      },
      fields: {
        shipper: 'Shipper',
        consignee: 'Consignee',
        notifyParty: 'Notify Party',
        vessel: 'Vessel',
        voyage: 'Voyage',
        portOfLoading: 'Port of Loading',
        portOfDischarge: 'Port of Discharge'
      }
    }
  },

  // Order Detail Page
  detail: {
    title: 'Order Details',
    copy: 'Copy',
    close: 'Close',
    back: 'Back',

    // Tasks
    tasks: {
      unfinished: 'Unfinished Tasks',
      completed: 'Completed Tasks',
      deadline: 'Deadline',
      taskName: 'Task Name',
      taskType: 'Task Type',
      executor: 'Executor',
      taskStatus: 'Task Status',
      pending: 'Pending',
      unassigned: 'Unassigned',
      system: 'System',
      process: 'Process',
      skip: 'Skip',
      view: 'View',
      generatedAt: 'Generated at',
      completedAt: 'Completed at',
      noUnfinishedTasks: 'No unfinished tasks',
      noCompletedTasks: 'No completed tasks'
    },

    // Tabs
    tabs: {
      messageBoard: 'Message Board',
      overview: 'Overview',
      files: 'Files'
    },

    viewTemplate: {
      title: 'Business Details'
    },

    // Collaborators
    collaborators: {
      title: 'Collaborator Contact Information',
      role: 'Role',
      name: 'Name',
      phone: 'Phone',
      email: 'Email',
      company: 'Company',
      noCollaborators: 'No collaborator information'
    },

    // Message
    message: {
      placeholder: 'Please enter your message',
      send: 'Send',
      attachment: 'Attachment Upload',
      noMessages: 'No messages',
      sending: 'Sending...',
      sendSuccess: 'Message sent successfully',
      sendFailed: 'Failed to send message',
      developing: 'Message function under development...'
    },

    feeDelete: {
      title: 'Confirm Delete',
      cancel: 'Cancel',
      confirm: 'Confirm Delete',
      message: 'Are you sure you want to delete fee <strong>{feeName}</strong>? This action cannot be undone.'
    },

    // Basic Info
    basicInfo: {
      title: 'Basic Information',
      vesselName: 'Vessel Name',
      carrier: 'Carrier',
      terminal: 'Terminal',
      cutoffTime: 'Cutoff Time',
      etd: 'ETD',
      eta: 'ETA',
      voyage: 'Voyage',
      orderType: 'Order Type',
      orderStatus: 'Order Status',
      priority: 'Priority',
      dateCreated: 'Order Created Time',
      createdBy: 'Created By',
      serviceCode: 'Service Code',
      portCallCode: 'Port Call Code',
      edit: 'Edit',
      save: 'Save',
      cancel: 'Cancel',
      placeholders: {
        vesselName: 'Please enter vessel name',
        voyage: 'Please enter voyage',
        carrier: 'Please select carrier',
        serviceCode: 'Please enter service code',
        terminal: 'Please select terminal',
        portCallCode: 'Please enter port call code',
        etd: 'Please select estimated departure time',
        eta: 'Please select estimated arrival time',
        cutoffTime: 'Please select cutoff time'
      },
      confirmCancel: {
        title: 'Confirm Cancel',
        content: 'There are changes in the data. If you cancel, the current modifications will be lost. Are you sure you want to cancel?',
        okText: 'Confirm Cancel',
        cancelText: 'Continue Editing'
      }
    },

    // Route Info
    routeInfo: {
      title: 'Route Information',
      pol: 'Port of Loading',
      pod: 'Port of Discharge',
      pdl: 'Destination Port',
      pot1: 'Transit Port',
      pot2: 'Transit Port 2',
      transitTime: 'Estimated Transit Time',
      shippingTerms: 'Shipping Terms',
      transportMode: 'Transport Mode',
      days: 'days',
      placeholders: {
        pol: 'Please select port of loading',
        pod: 'Please select port of discharge',
        pdl: 'Please select destination port',
        pot1: 'Please select transit port 1',
        pot2: 'Please select transit port 2',
        transitTime: 'Please enter estimated transit time',
        shippingTerms: 'Please select shipping terms',
        transportMode: 'Please select transport mode'
      }
    },

    // Cargo Info
    cargoInfo: {
      title: 'Cargo Information',
      cargoName: 'Cargo Name',
      quantity: 'Quantity',
      unit: 'Unit',
      grossWeight: 'Gross Weight',
      volume: 'Volume',
      cargoType: 'Type',
      details: 'Details',
      dangerousGoods: 'Dangerous Goods',
      view: 'View',
      noCargoInfo: 'No cargo information',
      placeholders: {
        cargoName: 'Please enter cargo name',
        quantity: 'Please enter quantity',
        unit: 'Please select unit',
        grossWeight: 'Please enter gross weight',
        volume: 'Please enter volume',
        cargoType: 'Please select type'
      }
    },

    // Container Info
    containerInfo: {
      title: 'Container Information',
      containerNo: 'Container No.',
      sealNo: 'Seal No.',
      containerType: 'Container Type',
      tareWeight: 'Tare Weight',
      containerStatus: 'Container Ownership',
      loadingPhotos: 'Loading Photos',
      noContainerInfo: 'No container information',
      unit: 'KGS'
    },

    // Reference Numbers
    referenceNumbers: {
      title: 'Reference Numbers',
      orderNo: 'Order No.',
      customNo: 'Custom No.',
      bookingNo: 'Booking No.',
      mblNo: 'MBL No.',
      hblNo: 'HBL No.',
      amsNo: 'AMS No',
      isfNo: 'ISF No',
      customReferenceNo: 'Reference No.'
    },

    // Related Parties
    relatedParties: {
      title: 'Related Party Information',
      partyType: 'Type',
      companyName: 'Company Name',
      contactPerson: 'Contact Person',
      address: 'Address',
      phone: 'Phone',
      email: 'Email',
      noRelatedParties: 'No related party information'
    },

    // Files
    files: {
      title: 'Files',
      searchPlaceholder: 'Search file name...',
      nodeFilter: 'Node Filter',
      downloadAll: 'Download All',
      fileName: 'File Name',
      uploadedBy: 'Uploaded By',
      uploadTime: 'Upload Time',
      download: 'Download',
      delete: 'Delete',
      deleteConfirm: 'Delete file "{fileName}"?',
      downloadFailed: 'Failed to download file',
      deleteFailed: 'Failed to delete file',
      fileType: 'File Type',
      fileTypeFallback: 'Other',
      noFiles: 'No files',
      operation: 'Operation',
      sourceFilter: 'Source',
      sourceSystem: 'System Documents',
      sourceUpload: 'Uploaded Files',
      systemSection: 'System Documents',
      uploadSection: 'Uploaded Files',
      itemCount: '{count} items',
      preview: 'Preview',
      previewTitle: 'File Preview',
      previewUnavailable: 'Preview is not available for this file type. Please download it.',
      previewFailed: 'Failed to preview file',
      upload: 'Upload File',
      noSystemDocs: 'No system documents',
      noUploadDocs: 'No uploaded files',
      uploadModalTitle: 'Upload File',
      uploadFileType: 'File Type',
      uploadFileTypePlaceholder: 'Select file type',
      uploadFileLabel: 'File',
      selectFile: 'Select File',
      uploadFilePlaceholder: 'Choose a file to upload',
      uploadSuccess: 'File uploaded successfully',
      uploadFailed: 'Failed to upload file',
      fileTypeRequired: 'Please select a file type',
      fileRequired: 'Please select a file to upload',
      orderIdMissing: 'Order ID is missing. Cannot upload file.',
      queryFailed: 'Failed to query files',
      retry: 'Retry',
      clientLocked: 'The order has passed pre-audit. Clients cannot upload or delete files.'
    },

    // Node Filter
    nodeFilter: {
      booking: 'Booking',
      customs: 'Customs',
      trucking: 'Trucking',
      warehouse: 'Warehouse',
      vgm: 'VGM',
      blAddition: 'BL Addition'
    },

    // Workflow Nodes
    workflowNodes: {
      title: 'Business Nodes',
      shippingNodes: 'Shipping Nodes',
      business: 'Business Nodes',
      shipping: 'Shipping Nodes',
      noNodes: 'No business node information',
      noShippingNodes: 'Shipping nodes function under development...',
      noTasks: 'No task information',
      expand: 'Expand Node Area',
      collapse: 'Collapse Node Area',
      node: 'Node',
      subNode: 'Sub-node'
    },

    // Skip Task
    skipTask: {
      title: 'Skip Task',
      confirm: 'Confirm Skip',
      cancel: 'Cancel',
      message: 'Skip task will mark this node as completed without further operations. Confirm to skip?',
      taskDescription: 'Skip Task: {taskName}',
      description: 'Skipping the task will directly mark this node as complete, no further operations required.',
      reasonLabel: 'Skip Reason',
      reasonPlaceholder: 'Please enter skip reason',
      reasonRequired: 'Please enter skip reason',
      taskInstanceIdNotExists: 'Task instance ID does not exist',
      success: 'Task skipped successfully',
      failed: 'Failed to skip task',
      notAllowed: 'This task is not allowed to be skipped',
      failedWithReason: 'Failed to skip task: {reason}'
    },

    // Confirm Process Task
    processTask: {
      title: 'Confirm Process Task',
      confirm: 'Confirm Process',
      cancel: 'Cancel',
      question: 'Confirm to process task: {taskName}?',
      description: 'After processing, this task will be marked as completed.',
      failed: 'Failed to process task',
      failedWithReason: 'Failed to process task: {reason}'
    },

    // Loading
    loading: {
      failed: 'Failed to load order details',
      notExists: 'Order ID does not exist'
    },

    // Save and Delete Messages
    saveMessages: {
      overviewSuccess: 'Overview information saved successfully',
      overviewFailed: 'Failed to save overview information',
      cargoSuccess: 'Cargo information saved successfully',
      cargoFailed: 'Failed to save cargo information',
      containerSuccess: 'Container information saved successfully',
      containerFailed: 'Failed to save container information',
      feeSuccess: 'Fee saved successfully',
      feeFailed: 'Failed to save fee',
      feeDeleteSuccess: 'Fee deleted successfully',
      feeDeleteFailed: 'Failed to delete fee',
      orderDetailNotExists: 'Order detail does not exist, cannot add fee'
    },

    // Task Processing Messages
    taskMessages: {
      taskInstanceIdNotExists: 'Task instance ID does not exist',
      taskInfoNotExists: 'Task information does not exist',
      processSuccess: 'Task processed successfully',
      taskProcessPrefix: 'Task process: '
    },

    // Map
    map: {
      title: 'Shipping Route',
      placeholder: '[Imagine I am a world map]'
    },

    // Receivable Fees
    receivableFees: {
      title: 'Receivable Fees',
      editModalTitle: 'Edit Receivable Fee',
      settlementObject: 'Settlement Object',
      feeName: 'Fee Name',
      unitPrice: 'Unit Price',
      quantity: 'Quantity',
      billingUnit: 'Billing Unit',
      currency: 'Currency',
      taxRate: 'Tax Rate',
      taxAmount: 'Tax Amount',
      inclusiveTaxPrice: 'Inclusive Tax Price',
      exclusiveTaxPrice: 'Exclusive Tax Price',
      invoiceTitle: 'Invoice Title',
      remarks: 'Remarks',
      createdBy: 'Created By',
      createTime: 'Create Time',
      modifiedBy: 'Modified By',
      modifyTime: 'Modify Time',
      edit: 'Edit',
      save: 'Save',
      cancel: 'Cancel',
      add: 'Add',
      delete: 'Delete',
      cancelDelete: 'Cancel Delete',
      noData: 'No receivable fee data',
      totalRecords: 'Total {count} records',
      willDeleteAfterSave: 'This fee will be deleted after saving',
      placeholders: {
        settlementObject: 'Please select settlement object',
        feeName: 'Please select or enter fee name',
        unitPrice: 'Unit Price',
        quantity: 'Quantity',
        billingUnit: 'Please select billing unit',
        currency: 'Please select currency',
        taxRate: 'Tax Rate',
        taxAmount: 'Tax Amount',
        inclusiveTaxPrice: 'Inclusive Tax Price',
        exclusiveTaxPrice: 'Exclusive Tax Price',
        invoiceTitle: 'Invoice Title',
        remarks: 'Remarks'
      }
    },

    // Payable Fees
    payableFees: {
      title: 'Payable Fees',
      editModalTitle: 'Edit Payable Fee',
      settlementObject: 'Settlement Object',
      feeName: 'Fee Name',
      unitPrice: 'Unit Price',
      quantity: 'Quantity',
      billingUnit: 'Billing Unit',
      currency: 'Currency',
      taxRate: 'Tax Rate',
      taxAmount: 'Tax Amount',
      inclusiveTaxPrice: 'Inclusive Tax Price',
      exclusiveTaxPrice: 'Exclusive Tax Price',
      invoiceTitle: 'Invoice Title',
      remarks: 'Remarks',
      createdBy: 'Created By',
      createTime: 'Create Time',
      modifiedBy: 'Modified By',
      modifyTime: 'Modify Time',
      edit: 'Edit',
      save: 'Save',
      cancel: 'Cancel',
      add: 'Add',
      delete: 'Delete',
      cancelDelete: 'Cancel Delete',
      noData: 'No payable fee data',
      totalRecords: 'Total {count} records',
      willDeleteAfterSave: 'This fee will be deleted after saving',
      placeholders: {
        settlementObject: 'Please select settlement object',
        feeName: 'Please select or enter fee name',
        unitPrice: 'Unit Price',
        quantity: 'Quantity',
        billingUnit: 'Please select billing unit',
        currency: 'Please select currency',
        taxRate: 'Tax Rate',
        taxAmount: 'Tax Amount',
        inclusiveTaxPrice: 'Inclusive Tax Price',
        exclusiveTaxPrice: 'Exclusive Tax Price',
        invoiceTitle: 'Invoice Title',
        remarks: 'Remarks'
      }
    }
  },

  // Order center operation history
  operationHistory: {
    emptyValue: '(Empty)',
    systemOperator: 'System',
    loadFailed: 'Failed to load operation history',
    labels: {
      opTime: 'Operation Time',
      opUser: 'Operator',
      opType: 'Operation Type',
      field: 'Changed Field',
      valueBefore: 'Before',
      valueAfter: 'After',
      opSource: 'Source'
    },
    types: {
      create: 'Create',
      update: 'Update',
      delete: 'Delete'
    },
    synthetic: {
      created: 'Created',
      existing: 'Existing',
      deleted: 'Deleted'
    },
    components: {
      shipment: 'Shipment',
      transportPlan: 'Transport Plan',
      transportConsignment: 'Transport Consignment',
      waybill: 'Waybill',
      logistics: 'Logistics',
      customs: 'Customs Information',
      contact: 'Contact Information',
      packageWd: 'Package Metrics',
      product: 'Product Information',
      consignmentInfo: 'Consignment Information',
      consignmentLogistics: 'Logistics',
      supplier: 'Supplier Information',
      container: 'Container Information',
      cargo: 'Cargo Information',
      vehicle: 'Vehicle Information',
      bulkCargo: 'Bulk Cargo Information',
      consignmentProduct: 'Product Information',
      consignmentProductDetail: 'Product Package Details',
      voyage: 'Voyage Information',
      packing: 'Packing List',
      trackingNode: 'Tracking Node'
    }
  },
  distributionShipment: {
    title: 'Distribution Shipment',
    placeholder: 'Distribution shipment module is under development',
    common: {
      yes: 'Yes',
      no: 'No',
      none: 'No data',
      actions: 'Actions',
      back: 'Back',
      cancel: 'Cancel',
      cancelShipment: 'Cancel',
      cancelPlan: 'Cancel Planning',
      freeze: 'Freeze',
      unfreeze: 'Unfreeze',
      edit: 'Edit',
      save: 'Save',
      print: 'Print',
      readOnly: 'Read-only',
      editable: 'Editable',
      partialEditable: 'Partially Editable',
      chooseRegion: 'Choose Region',
      confirmRegion: 'Confirm Region',
      addRow: 'Add Row',
      new: 'New',
      multiCurrency: 'Multiple Currencies',
      listSeparator: ', '
    },
    create: {
      titlePrefix: 'Distribution Shipment: '
    },
    cancelModal: {
      title: 'Cancel Distribution Shipment',
      reasonLabel: 'Cancellation Reason',
      reasonPlaceholder: 'Enter the cancellation reason',
      reasonRequired: 'Enter the cancellation reason'
    },
    tabs: {
      basic: 'Basic',
      tracking: 'Tracking',
      fee: 'Fees'
    },
    manualPush: {
      button: 'Push Estimated Fees',
      statusLoading: 'Checking push status',
      enabledTip: 'Only unsuccessful estimated fee bill groups will be pushed',
      disabledTip: 'Estimated fees cannot be pushed manually now',
      confirmTitle: 'Confirm Manual Push',
      confirmContent: 'Unsuccessful estimated fee bill groups for this shipment will be pushed again. Continue?',
      success: '{count} estimated fee bill group(s) pushed successfully',
      partialFailed: 'Estimated fee push partially failed: {successCount} succeeded, {failedCount} failed',
      partialFailedTitle: 'Some bill groups failed to push',
      resultCount: '{successCount} succeeded, {failedCount} failed',
      resultTitle: 'Estimated Fee Push Result',
      retryFailed: 'Retry Failed Bill Groups',
      retryTip: 'Only the failed bill groups above will be retried. Successful groups will not be pushed again.',
      requiredFieldsMissing: 'Required fields are missing from the NS estimated bill',
      billMissingFields: 'Bill information: {fields}',
      expenseLineMissingFields: 'Expense line {index}: {fields}',
      fieldLabels: {
        operationType: 'Operation Type',
        fhNo: 'FH No.',
        subsidiary: 'Subsidiary',
        supplierId: 'Supplier ID',
        currency: 'Currency',
        journalType: 'Journal Type',
        accrualPeriodDate: 'Accrual Period Date',
        expenseDetails: 'Expense Details',
        estimatedAmount: 'Estimated Amount',
        businessType: 'Business Type',
        feeType: 'Fee Type',
        department: 'Department',
        costCenter: 'Cost Center',
        saleStyle: 'Sales Model',
        bal1: 'Level 1 Region',
        bal2: 'Level 2 Region',
        bal3: 'Level 3 Region',
        brparties: 'Business Party'
      },
      columns: {
        subsidiary: 'Subsidiary',
        currency: 'Currency',
        supplier: 'Supplier',
        reason: 'Failure Reason'
      },
      failed: 'Failed to push estimated fees manually'
    },
    sections: {
      basicInfo: 'Order Basic Information',
      logisticsInfo: 'Logistics Information',
      packageMetrics: 'Package Metrics',
      relatedPlanInfo: 'Related Plan Information',
      businessCategory: 'Business Category',
      businessTags: 'Business Tags',
      contactAddressInfo: 'Contact Address Information',
      productInfo: 'Product Information',
      tracking: 'Tracking',
      feeDetail: 'Fee Details',
      operationHistory: 'Operation History'
    },
    tags: {
      logisticsEditable: 'Logistics information editable',
      tmsEditable: 'TMS Editable'
    },
    empty: {
      tracking: 'No tracking records',
      trackingNode: 'No tracking nodes',
      fee: 'No fee details',
      operationHistory: 'No operation history',
      relatedPlan: 'No related transport plans',
      contactAddress: 'No contact address information'
    },
    trackingTab: {
      planDocType: 'Transport Plan',
      unplannedPlan: 'No related transport plan',
      currentTrackingNode: 'Current Tracking Node',
      separator: ': ',
      waybillCount: '{count} waybill(s)',
      noPlan: 'No transport plans',
      noWaybill: 'No related waybills',
      loadFailed: 'Failed to load tracking data'
    },
    export: {
      productFilename: 'Distribution_Shipment_Product_Info',
      productTitle: 'Distribution Shipment Product Information',
      unnamed: 'Unnamed'
    },
    list: {
      filterTitle: 'Distribution Shipment Filters',
      create: 'New Distribution Shipment',
      exportTitle: 'Distribution Shipment List',
      total: 'Total {count}',
      chainHint: 'Shipment planning flows into transport plans, consignment orders, and waybills'
    },
    outboundNotice: {
      button: 'Outbound Notice',
      confirmTitle: 'Outbound Notice',
      confirmContent: 'Confirm push?',
      selectRequired: 'Select at least one distribution shipment',
      precheckFailedTitle: 'Outbound Notice Validation Failed',
      precheckFailed: 'Failed to validate outbound notice',
      pushFailed: 'Failed to push outbound notice',
      resultTitle: 'Outbound Notice Push Result',
      resultSummary: '{successCount} succeeded, {failureCount} failed',
      notifiedCount: 'Notified +{count}',
      notNotified: 'Not Notified',
      success: 'Success',
      failure: 'Failed',
      columns: {
        fhNo: 'Distribution Shipment No.',
        status: 'Result',
        failureReason: 'Failure Reason'
      }
    },
    plan: {
      title: 'Shipment Planning',
      okText: 'Generate Plan',
      submitApproval: 'Submit for Approval',
      cancel: 'Cancel',
      shipmentNo: 'Shipment Request No.',
      destCountry: 'Destination Country',
      expectArrivalDate: 'Expected Delivery Time',
      clearanceTradeTerm: 'Trade Term (Clearance)',
      customerConsignment: 'Customer-designated',
      expectTransportMode: 'Expected Transport Service Type',
      mainTransportMode: 'Main Transport Service Type',
      splitStrategy: 'Split Method',
      expressSupplier: 'Express Carrier',
      expressNo: 'Upstream Express No.',
      splitSegment: 'Split by Segment',
      generatedPlanCount: 'Plans to Generate',
      countUnit: 'plan(s)',
      autoCreateContent: 'Auto-created Content',
      autoCreateTransportPlanConsignment: 'Transport Plan + Consignment Order',
      autoCreateTransportPlanConsignmentWaybill: 'Transport Plan + Consignment Order + Waybill',
      consignmentStatus: 'Consignment Status',
      consignmentEntrusted: 'Entrusted',
      consignmentNotEntrusted: 'Not Entrusted',
      shipWarehouse: 'Shipping Warehouse',
      consigneeCity: 'Receiving Address / City',
      selectPlaceholder: 'Please select',
      searchSelect: 'Search and select {field}',
      splitYes: 'Yes (three segments)',
      splitNo: 'No (full route)',
      splitDisabledExpress: 'Express does not support segment split; full route is used',
      splitDisabledUnsupportedMode: '{mode} does not support segment split; full route is used',
      splitDisabledCustomerConsignment: 'Customer-designated shipment is not split',
      missingAddressTip: 'At least one shipper address and one consignee address are required before generating transport plans.',
      fullTip: 'The current method is full-route direct and will generate 1 plan: full route (shipping warehouse → receiving address). Confirm the route information before submitting.',
      splitTip: 'The current method is a three-segment split ({endpointMode}) and will generate 3 plans: pre-carriage (shipping warehouse → origin), main carriage (origin → destination), and last mile (destination → receiving address). Confirm the route information before submitting.',
      twoPreMainTip: 'The current method is a two-plan split (pre-carriage + main carriage), {endpointMode}, and will generate 2 plans: pre-carriage + main carriage (shipping warehouse → destination), and last mile (destination → receiving address). Confirm the route information before submitting.',
      twoMainLastTip: 'The current method is a two-plan split (main carriage + last mile), {endpointMode}, and will generate 2 plans: pre-carriage (shipping warehouse → origin), and main carriage + last mile (origin → receiving address). Confirm the route information before submitting.',
      splitStrategyOptions: {
        full: 'Full route',
        three: 'Three segments',
        twoPreMain: 'Two plans: pre-carriage + main carriage {\'|\'} last mile',
        twoMainLast: 'Two plans: pre-carriage {\'|\'} main carriage + last mile'
      },
      expressCompleteTip: 'The related transport plan, consignment order, and waybill will be created automatically.',
      expressMissingNoTip: 'No express waybill number is available from WLN. The waybill will not be created; add it manually after the number is available.',
      expressMissingSupplierTip: 'Express carrier is empty. The consignment order will remain not entrusted; add the carrier before creating the consignment.',
      expressMissingBothTip: 'Express carrier and waybill number are both empty. Complete consignment and waybill creation manually after filling them in.',
      portPrefix: {
        loading: 'Origin',
        discharge: 'Destination'
      },
      endpointType: {
        country: 'Country',
        port: 'Port'
      },
      endpointMode: {
        country: 'country mode',
        port: 'port mode',
        mixed: 'mixed country/port mode'
      },
      portType: {
        airport: 'Airport',
        landPort: 'Land Port',
        railwayStation: 'Railway Station',
        seaport: 'Port'
      }
    },
    subtable: {
      empty: 'No transport plans',
      planDetailTodo: 'Transport plan detail will be handled separately',
      planDetailTodoWithNo: 'Transport plan detail will be integrated later: {no}',
      columns: {
        planNo: 'Plan No.',
        segment: 'Transport Segment',
        mainTransportMode: 'This Segment Transport Service Type',
        originPoint: 'Segment Origin',
        destPoint: 'Segment Destination',
        mainlineOriginCountry: 'Origin Country',
        mainlineDestCountry: 'Destination Country',
        portOfLoading: 'Port of Loading',
        portOfDischarge: 'Port of Discharge',
        planStatus: 'Plan Status',
        trackingStatus: 'Tracking Status'
      },
      segment: {
        preCarriage: 'Pre-carriage',
        mainCarriage: 'Main Carriage',
        lastMile: 'Last Mile',
        fullCarriage: 'Full Route'
      },
      planStatus: {
        inTransit: 'In Transit'
      }
    },
    packageMetrics: {
      source: 'Data Source',
      totalQty: 'Total Units',
      totalBoxQty: 'Cartons',
      totalPalletQty: 'Pallets',
      totalVolume: 'Total Volume',
      totalWeight: 'Total Weight',
      chargeWeight: 'Chargeable Weight',
      length: 'Length',
      width: 'Width',
      height: 'Height',
      expectSource: 'Theoretical Value',
      warehouseSource: 'Warehouse Data',
      supplierSource: 'Forwarder Data'
    },
    table: {
      requiredTitle: '{field} *'
    },
    contactAddress: {
      warehouseLoadFailed: 'Failed to load the warehouse address. The previous address was cleared. Select the warehouse again or enter the address manually.',
      warehouseEnglishMissing: 'The selected warehouse has no maintained English version. The address was cleared. Please enter it manually.',
      warehouseCountryMissing: 'The warehouse country or its required translation was not found in tenant master data. Please select the country manually.',
      importExcel: 'Import Excel',
      importDisabledTip: 'Batch import is only available for domestic express shipments',
      aiRecognize: 'AI Recognize',
      aiTitle: 'AI Address Recognition',
      aiTip: 'Enter complete addresses. The system will parse them into structured fields. Multiple addresses are supported.',
      aiPlaceholder: 'Paste raw address text, one address per line. Example:\n8F, Block A, South Tech Park, Nanshan District, Shenzhen, Guangdong, China, Zhang Wei, 13800138000',
      aiTargetLabel: 'Fill recognition results into:',
      cancel: 'Cancel',
      startRecognition: 'Start Recognition',
      aiEmptyInput: 'Enter raw address text',
      importInvalidType: 'Only xls and xlsx files are supported',
      importNoFile: 'Select an import file',
      importFailed: 'Failed to import contact addresses',
      importPartialError: 'The import file has {count} error(s): {detail}',
      applySuccess: 'Filled {count} contact address(es). Please verify them in the table.',
      applySkipped: '{count} address(es) were skipped because the address type reached its limit',
      aiFailed: '{count} address(es) failed to recognize. Please check and retry.',
      countryUnmatched: 'These countries were not found in master data. Please select them manually: {countries}',
      noRowsApplied: 'No writable contact address found',
      importErrors: {
        emptyFile: 'The Excel file is empty',
        missingHeaders: 'Missing required header(s): {headers}',
        illegalType: 'Invalid address type. Shipper, consignee, notify, customs shipper/consignee, clearance shipper/consignee, actual pickup and actual delivery addresses are supported.',
        noData: 'The Excel file has no importable data',
        unknown: 'Failed to parse import row'
      },
      partyType: {
        shipper: 'Shipper Address',
        consignee: 'Consignee Address',
        notify: 'Notify Address',
        customsShipper: 'Customs Shipper Address',
        customsConsignee: 'Customs Consignee Address',
        clearanceShipper: 'Clearance Shipper Address',
        clearanceConsignee: 'Clearance Consignee Address',
        actualPickup: 'Actual Pickup Address',
        actualDelivery: 'Actual Delivery Address'
      }
    },
    messages: {
      queryFailed: 'Failed to query distribution shipments',
      emptyShipmentData: 'Distribution shipment data is empty',
      emptyDetailId: 'Distribution shipment ID is empty; cannot view details',
      emptyPlanId: 'Distribution shipment ID is empty; cannot plan shipment',
      detailLoadFailed: 'Failed to load distribution shipment detail',
      operationHistoryLoadFailed: 'Failed to load operation history',
      enteredPlanApproval: 'Shipment planning approval has been submitted',
      transportPlanGenerated: 'Transport plan generated',
      planFailed: 'Shipment planning failed',
      emptyEditId: 'Distribution shipment ID is empty; cannot edit',
      copyOfflineOnly: 'Only offline shipments can be copied',
      emptyCopyId: 'Distribution shipment ID is empty; cannot copy',
      noExportData: 'No distribution shipment data available for export',
      noProductExportData: 'No product information available for export',
      csvExportSuccess: 'CSV exported successfully',
      excelExportSuccess: 'Excel exported successfully',
      planAlreadyGenerated: 'Transport plans already exist. Cancel shipment planning before regenerating.',
      planApprovalPending: 'Shipment planning approval is pending',
      planStatusDisabled: 'Current status does not allow shipment planning',
      cancelSourceUnsupported: 'Distribution shipments from the current source cannot be cancelled',
      cancelActivePlanExists: 'Cancel or delete all related transport plans before cancelling the shipment',
      cancelStatusDisabled: 'Current status does not allow cancellation',
      cancelPlanStatusDisabled: 'Only in-operation shipments or OMS-blocked frozen shipments can cancel planning after splitting',
      cancelShipmentConfirmTitle: 'Confirm Cancellation',
      cancelShipmentConfirmContent: 'The distribution shipment will be marked as cancelled. Continue?',
      cancelShipmentSuccess: 'Distribution shipment cancelled',
      cancelShipmentFailed: 'Failed to cancel distribution shipment',
      cancelFailureTitle: 'Cancellation Failed',
      wmsOrderNotFound: 'The shipment number does not exist in WMS.',
      cancelFailureGuidance: 'TMS cannot cancel this shipment. Please coordinate offline before requesting cancellation again.',
      cancelRequestFailed: 'The cancellation request failed or timed out. Please verify the result offline.',
      tmsOnlyCancelConfirmContent: 'The shipment number does not exist in WMS. You may cancel the shipment in TMS only, without synchronizing to WMS.',
      tmsOnlyCancelOkText: 'Cancel TMS Only',
      tmsOnlyCancelFailed: 'Failed to cancel the distribution shipment in TMS',
      cancelPlanConfirmTitle: 'Confirm Cancel Planning',
      cancelPlanConfirmContent: 'Related transport plans will be cancelled and shipment planning can be regenerated. Continue?',
      cancelPlanSuccess: 'Shipment planning cancelled',
      cancelPlanFailed: 'Failed to cancel shipment planning',
      freezeConfirmTitle: 'Confirm Freeze',
      freezeConfirmContent: 'The distribution shipment will be frozen and downstream operations will be paused. Continue?',
      freezeSuccess: 'Distribution shipment frozen',
      freezeFailed: 'Failed to freeze distribution shipment',
      unfreezeConfirmTitle: 'Confirm Unfreeze',
      unfreezeConfirmContent: 'The distribution shipment will be restored to in-operation. Continue?',
      unfreezeSuccess: 'Distribution shipment unfrozen',
      unfreezeFailed: 'Failed to unfreeze distribution shipment',
      emptyLoadDetailId: 'Distribution shipment ID is empty; cannot load detail',
      readonlyByStatus: 'Current status is not editable. Displaying in read-only mode.',
      copySuccessWithNo: 'Copied distribution shipment {no}. Please confirm before saving.',
      copySuccess: 'Copied distribution shipment. Please confirm before saving.',
      externalEditScopeHint: 'Destination warehouse, warehouse code, expected shipment date, goods ready time, expected arrival date, logistics information, business tags, and contact addresses are editable; other fields are read-only',
      calculatedByProducts: 'Calculated by products: {amount}',
      theoreticalChargeWeightSkippedProducts: 'SKU {skuCodes} has incomplete package data. Missing metrics were excluded from the theoretical package calculation.',
      theoreticalChargeWeightCalculationFailed: 'Unable to calculate theoretical package metrics automatically. Enter them manually.',
      businessDataLoadFailed: 'Failed to load {types}. You can enter values manually. Please try again later.',
      businessDataNotConfigured: 'No platform data is configured for {types}. You can enter values manually.',
      transportPlanUpdated: 'Transport plan updated',
      saveSuccess: 'Saved successfully'
    },
    businessDataTypes: {
      businessClass: 'business category',
      costCenter: 'cost center',
      salesMode: 'sales mode',
      businessDepartment: 'business department',
      region: 'region',
      separator: ', '
    },
    placeholders: {
      input: 'Enter {field}',
      batchInput: 'Enter {field}; separate multiple values with comma/space',
      select: 'Select {field}',
      startDate: 'Start date',
      endDate: 'End date',
      startTime: 'Start time',
      endTime: 'End time',
      selectOrderTypeFirst: 'Select order type first',
      searchCustomerName: 'Search customer short/name',
      searchCustomerShortName: 'Search customer short name',
      customerAutoFill: 'Filled after customer selection',
      searchCustomerCode: 'Search customer code/name',
      systemAutoFill: 'Filled by system',
      destCountryAutoFill: 'Filled from consignee country',
      tradeChain: 'Company short name 1 -> Company short name 2 -> Company short name 3',
      selectOnly: 'Please select',
      selectOrInputCurrency: 'Select or enter currency',
      selectOrInputTotalAmount: 'Select or enter total amount',
      selectOrInputTotalTax: 'Select or enter total tax',
      selectOrInputClearanceAmount: 'Select or enter clearance amount',
      regionCodePath: 'Enter three region code levels separated by /',
      regionPath: 'Enter three region levels separated by /',
      inputTag: 'Press Enter to add',
      selectOrInputProvince: 'Select or enter province/state',
      selectOrInputDistrict: 'Select or enter district'
    },
    validation: {
      requiredSelect: 'Select {field}',
      requiredInput: 'Enter {field}',
      regionPathsRequiredTogether: 'Region code and region must be entered together',
      regionPathInvalid: 'Region code and region must each contain exactly three non-empty levels separated by /',
      regionPickerIncomplete: 'Select all three region levels',
      contactShipperRequired: 'At least one shipper address is required',
      contactConsigneeRequired: 'At least one consignee address is required',
      contactRowIncomplete: 'Complete contact address row {row}: {fields}',
      productRowIncomplete: 'Complete product row {row}: {fields}'
    },
    options: {
      source: {
        wln: 'WangLiNiu',
        offline: 'Offline'
      },
      mainStatus: {
        created: 'Created',
        approving: 'Approving',
        approved: 'Approved',
        inOperation: 'In Operation',
        completed: 'Completed',
        frozen: 'Frozen',
        cancelled: 'Cancelled'
      },
      splitStatus: {
        notSplit: 'Not Split',
        split: 'Split'
      },
      transportMode: {
        seaFcl: 'Sea FCL',
        seaLcl: 'Sea LCL',
        air: 'Air',
        land: 'Road',
        rail: 'Rail',
        express: 'Express',
        other: 'Other'
      },
      logisticsDirection: {
        forward: 'Forward',
        reverse: 'Reverse'
      },
      customsType: {
        none: 'No Customs',
        formal: 'Formal Customs',
        expressClearance: 'Express Clearance'
      },
      orderType: {
        sales: 'Sales Order',
        nonSales: 'Non-sales Order'
      },
      docSubtype: {
        directSales: 'Direct Sales',
        distribution: 'Distribution',
        transfer: 'Transfer',
        sample: 'Sample',
        returned: 'Return'
      },
      bizDept: {
        northAmerica: 'North America',
        international: 'International',
        domestic: 'Domestic'
      },
      goodsType: {
        standardProduct: 'Standard Product',
        marketingMaterial: 'Marketing Material',
        rawMaterial: 'Raw Material',
        gift: 'Gift'
      },
      salesPlatform: {
        offline: 'Offline',
        other: 'Other'
      },
      partyType: {
        shipper: 'Shipper',
        consignee: 'Consignee',
        notify: 'Notify Party',
        customsShipper: 'Customs Shipper Address',
        customsConsignee: 'Customs Consignee Address',
        clearanceShipper: 'Clearance Shipper Address',
        clearanceConsignee: 'Clearance Consignee Address',
        actualPickup: 'Actual Pickup Address',
        actualDelivery: 'Actual Delivery Address'
      },
      operationType: {
        create: 'Create',
        update: 'Update',
        delete: 'Delete'
      }
    },
    fields: {
      fhNo: 'Shipment No.',
      businessCategory: 'Business Category',
      fhCateg: 'Business Category Code',
      fhCategName: 'Business Category Name',
      orderNo: 'Order No.',
      orderStatus: 'Order Status',
      mainStatus: 'Main Status',
      splitStatus: 'Split Status',
      currentTrackingNode: 'Current Tracking Node',
      source: 'Source',
      orderType: 'Order Type',
      docSubtype: 'Document Subtype',
      goodsType: 'Goods Type',
      bizTags: 'Business Tags',
      customer: 'Customer',
      customerNo: 'Customer No.',
      customerShortName: 'Customer Short Name',
      customerCode: 'Customer Code',
      cusShortName: 'Customer Short Name',
      cusName: 'Customer Name',
      cusNo: 'Customer Code',
      regionCodePath: 'Region Code',
      regionPath: 'Region',
      regionLevelOne: 'Region Level 1',
      regionName: 'Region Level 1 Name',
      regionLevelTwo: 'Region Level 2',
      regionLevel2Name: 'Region Level 2 Name',
      regionLevelThree: 'Region Level 3',
      regionLevel3Name: 'Region Level 3 Name',
      costCenter: 'Cost Center Code',
      costCenterName: 'Cost Center',
      bizDept: 'Business Department Code',
      departmentName: 'Business Department',
      salesType: 'Sales Mode Code',
      salesTypeName: 'Sales Mode Name',
      salesPlatform: 'Sales Platform',
      originWhCode: 'Shipping Warehouse Code',
      originWh: 'Shipping Warehouse',
      destinationWhCode: 'Destination Warehouse Code',
      destinationWh: 'Destination Warehouse',
      expectShipDate: 'Expected Ship Date',
      goodsReadyTime: 'Goods Ready Time',
      outboundNoticeSuccessCount: 'Outbound Notice Status',
      actShipDate: 'Actual Ship Time',
      expectArrivalDate: 'Expected Arrival Date',
      actArrivalDate: 'Actual Arrival Time',
      originCreateTime: 'Source Created Time',
      originCreator: 'Source Creator',
      consignmentOrder: 'Consignment Order',
      orderReferenceRemark: 'Order Reference (PO/FBA/SW)',
      orderRemark: 'Order Remark',
      cancelReason: 'Cancellation Reason',
      logisticsOperator: 'Logistics Operator',
      logisticsOperatorText: 'Source Logistics Specialist',
      createdAt: 'Business Created Time',
      updatedAt: 'Business Updated Time',
      systemCreatedAt: 'System Created Time',
      systemUpdatedAt: 'System Updated Time',
      insure: 'Insurance Purchased',
      pickingTime: 'Picking Time',
      pickingUser: 'Picking Operator',
      pickingRemark: 'Picking Remark',
      crossBorder: 'Cross-border',
      palletized: 'Palletized',
      logisticsDirection: 'Logistics Direction',
      expectTransportMode: 'Expected Transport Service Type',
      mainTransportMode: 'Main Transport Service Type',
      splitSegment: 'Split by Segment',
      splitStrategy: 'Split Strategy',
      customerConsignment: 'Customer-designated',
      tradeCountry: 'Trade Country',
      tradeCountryEn: 'Trade Country (EN)',
      destCountry: 'Destination Country',
      destCountryEn: 'Destination Country (EN)',
      tradeChain: 'Trade Chain',
      customsTradeTerm: 'Trade Term (Customs)',
      clearanceTradeTerm: 'Trade Term (Clearance)',
      transportTerms: 'Transport Terms',
      expressSupplier: 'Express Carrier',
      expressNo: 'Upstream Express No.',
      customsFlag: 'Customs Declaration',
      customsType: 'Customs Type',
      currency: 'Currency',
      taxCurrency: 'Tax Currency',
      totalAmount: 'Total Amount',
      totalTax: 'Total Tax',
      clearanceTotalAmountCurrency: 'Clearance Amount Currency',
      clearanceTotalAmount: 'Clearance Amount',
      exchangeRate: 'Exchange Rate',
      hsCode: 'HS Code',
      originCountry: 'Country of Origin',
      bizRemark: 'Transport Remark',
      region: 'Region',
      totalAmountCurrency: 'Total Amount Currency',
      totalTaxCurrency: 'Total Tax Currency',
      partyType: 'Type',
      company: 'Company',
      contactName: 'Contact',
      phone: 'Phone',
      country: 'Country',
      province: 'Province/State',
      city: 'City',
      district: 'District/County',
      town: 'Town/Street',
      address: 'Detailed Address',
      postcode: 'Postcode',
      skuCode: 'SKU',
      productName: 'Product Name',
      salesOrderNo: 'Upstream Order No.',
      docType: 'Document Type',
      productModel: 'Product Model',
      brand: 'Brand',
      specialAttributes: 'Special Product Attributes',
      barCode: 'UPC Barcode',
      pcsQty: 'Count',
      boxGauge: 'Outer Box Spec',
      stdBoxQty: 'Standard Box Qty',
      actualBoxQty: 'Actual Box Qty',
      packingMethod: 'Packing Method',
      cisOsNo: 'CIS OS No.',
      cnDesc: 'Chinese Description',
      enDesc: 'English Description',
      customsDecl: 'Customs Declaration Elements',
      amountOrigin: 'Original Amount',
      unitPriceOrigin: 'Original Unit Price',
      unitPrice: 'Tax-included Unit Price',
      unitPriceNet: 'Tax-excluded Unit Price',
      amtNet: 'Tax-excluded Amount',
      amtFh: 'Tax-included Amount',
      tax: 'Tax',
      taxRatio: 'Tax Rate',
      unitPriceAg: 'Clearance Tax-excluded Unit Price',
      amountNetAg: 'Clearance Tax-excluded Amount',
      amountTaxAg: 'Clearance Tax',
      amountAllAg: 'Clearance Tax-included Amount',
      currencyAg: 'Clearance Currency',
      unitPriceBg: 'Customs Tax-excluded Unit Price',
      amountNetBg: 'Customs Tax-excluded Amount',
      amountTaxBg: 'Customs Tax',
      amountAllBg: 'Customs Tax-included Amount',
      currencyBg: 'Customs Currency',
      infoType: 'Information Type',
      buyerNameCn: 'Buyer Name (CN)',
      buyerNameEn: 'Buyer Name (EN)',
      buyerLocationCn: 'Buyer Address (CN)',
      buyerLocationEn: 'Buyer Address (EN)',
      buyerTelephone: 'Buyer Phone',
      buyerFax: 'Buyer Fax',
      sellerNameCn: 'Seller Name (CN)',
      sellerNameEn: 'Seller Name (EN)',
      sellerLocationCn: 'Seller Address (CN)',
      sellerLocationEn: 'Seller Address (EN)',
      importer: 'Importer',
      declarationAgentNameEn: 'Declaration Agent Name (EN)',
      taxNo: 'Tax No.',
      einNo: 'EIN',
      taxAddress: 'Tax Address',
      sellerTelephone: 'Seller Phone',
      sellerFax: 'Seller Fax',
      sellerPost: 'Importer Postcode',
      sellerWebsite: 'Importer Website',
      subsidiary: 'Subsidiary',
      locationCountry: 'Country',
      addressId: 'Address ID',
      innerPo: 'Inner PO',
      popInvoiceType: 'Marketing Material Clearance Template Type',
      popMemo: 'Clearance Invoice Remark',
      volumeUnit: 'Volume Unit',
      weightUnit: 'Weight Unit',
      lengthUnit: 'Length Unit',
      weightCount: 'Chargeable Weight Unit',
      totalQtyExpect: 'Expected Total Quantity',
      totalBoxQtyExpect: 'Expected Total Boxes',
      totalPalletQtyExpect: 'Expected Total Pallets',
      totalVolumeExpect: 'Expected Total Volume',
      totalWeightExpect: 'Expected Total Weight',
      totalWeightCountExpect: 'Expected Chargeable Weight',
      lengthExpect: 'Expected Length',
      widthExpect: 'Expected Width',
      heightExpect: 'Expected Height',
      totalQtyWh: 'Warehouse Total Quantity',
      totalBoxQtyWh: 'Warehouse Total Boxes',
      totalPalletQtyWh: 'Warehouse Total Pallets',
      wmsPickingTotalPackages: 'WMS Picking Packages',
      wmsOutboundTotalPackages: 'WMS Outbound Packages',
      wmsPalletTotalPackages: 'WMS Pallet Packages',
      totalVolumeWh: 'Warehouse Total Volume',
      totalWeightWh: 'Warehouse Total Weight',
      totalWeightCountWh: 'Warehouse Chargeable Weight',
      lengthWh: 'Warehouse Length',
      widthWh: 'Warehouse Width',
      heightWh: 'Warehouse Height',
      totalQtySupplier: 'Supplier Total Quantity',
      totalBoxQtySupplier: 'Supplier Total Boxes',
      totalPalletQtySupplier: 'Supplier Total Pallets',
      totalVolumeSupplier: 'Supplier Total Volume',
      totalWeightSupplier: 'Supplier Total Weight',
      totalWeightCountSupplier: 'Supplier Chargeable Weight',
      lengthSupplier: 'Supplier Length',
      widthSupplier: 'Supplier Width',
      heightSupplier: 'Supplier Height',
      productTotalQuantity: 'Total Quantity',
      productAmount: 'Product Amount',
      customsTotalAmount: 'Customs Value',
      transportSegment: 'Transport Segment',
      logSeq: 'No.',
      opTime: 'Operation Time',
      opUser: 'Operator',
      opSource: 'Source',
      opType: 'Operation Type',
      field: 'Changed Field',
      valueBefore: 'Before',
      valueAfter: 'After',
      actions: 'Actions'
    }
  },

  // Tracking Query
  trackingQuery: {
    title: 'Tracking Query',
    queryTypePlaceholder: 'Select query type',
    queryTypeDocument: 'HBL NO./ MBL NO./ Reference No./ Cargoware Job No.',
    queryTypeContainer: 'Container No.',
    inputPlaceholder: 'Separate multiple numbers with commas, spaces or new lines',
    search: 'Search',
    detail: 'Detail',
    emptyTip: 'Enter a number and click Search',
    limitTip: 'Up to 20 numbers are supported; extras were trimmed',
    notFound: 'No matching orders',
    unmatchedTip: 'Unmatched numbers: {numbers}',
    matched: 'Order No.: {orderNo}',
    matchedCount: '{count} order(s) matched'
  },

  // Chassis Query
  chassisQuery: {
    title: 'Chassis Query',

    // Filter Fields
    filters: {
      jobNo: 'Bill of Lading No.',
      jobNoPlaceholder: 'Enter Bill of Lading No. (MB/L NO+S/O NO)',
      chassisNo: 'Chassis No.',
      chassisNoPlaceholder: 'Enter Chassis No.',
      vessel: 'Vessel',
      vesselPlaceholder: 'Enter Vessel Name',
      voyage: 'Voyage',
      voyagePlaceholder: 'Enter Voyage No.',
      pol: 'Port of Loading',
      polPlaceholder: 'Select Port of Loading',
      pod: 'Port of Discharge',
      podPlaceholder: 'Select Port of Discharge',
      sailingDate: 'Sailing Date',
      sailingDatePlaceholder: 'Select Date Range',
      abnormalStatus: 'Abnormal Status',
      abnormalStatusPlaceholder: 'Select Abnormal Status',
      all: 'All',
      has: 'Yes',
      none: 'No'
    },

    // Table Columns
    columns: {
      jobNo: 'Bill of Lading No.',
      chassisNo: 'Chassis No.',
      vehicleNo: 'Vehicle No.',
      pol: 'Port of Loading',
      pod: 'Port of Discharge',
      vesselVoyage: 'Vessel/Voyage',
      receivedDate: 'Received Date',
      preLoadSurveyDate: 'Pre-load Survey Date',
      manual: 'Manual',
      spareTire: 'Spare Tire',
      key: 'Key/Quantity',
      abnormalStatus: 'Abnormal Status',
      actions: 'Actions',
      view: 'View'
    },

    // Detail Drawer
    detail: {
      drawerTitle: 'Chassis Details',

      // Shipping Data
      shippingData: {
        title: 'Shipping Data',
        pol: 'Port of Loading',
        pod: 'Port of Discharge',
        vesselVoyage: 'Vessel/Voyage',
        receivedDate: 'Received Date',
        preLoadSurveyDate: 'Pre-load Survey Date'
      },

      // Vehicle Information
      vehicleInfo: {
        title: 'Vehicle Information',
        jobNo: 'Bill of Lading No.',
        chassisNo: 'Chassis No.',
        vehicleNo: 'Vehicle No.',
        manual: 'Manual',
        spareTire: 'Spare Tire',
        keyQuantity: 'Key/Quantity',
        abnormalDescription: 'Abnormal Description',
        adjustDescription: 'Adjustment Description',
        abnormalPhotos: 'Abnormal Photos'
      },

      // Basic Information (kept for backward compatibility)
      basicInfo: {
        title: 'Basic Information',
        jobNo: 'Bill of Lading No.',
        chassisNo: 'Chassis No.',
        vehicleNo: 'Vehicle No.',
        brandModel: 'Brand & Model',
        yearOfManufacture: 'Year of Manufacture',
        color: 'Color',
        mileage: 'Mileage',
        engineNo: 'Engine No.'
      },

      // Port & Schedule Information
      portSchedule: {
        title: 'Port & Schedule Information',
        pol: 'Port of Loading',
        pod: 'Port of Discharge',
        vessel: 'Vessel Name',
        voyage: 'Voyage No.',
        eta: 'Estimated Time of Arrival',
        etd: 'Estimated Time of Departure',
        ata: 'Actual Time of Arrival',
        atd: 'Actual Time of Departure'
      },

      // Inspection Information
      inspection: {
        title: 'Inspection Information',
        receivedDate: 'Received Date',
        preLoadSurveyDate: 'Pre-load Survey Date',
        fuelLevel: 'Fuel Level',
        inspector: 'Inspector',
        inspectionLocation: 'Inspection Location'
      },

      // Vehicle Accessories
      accessories: {
        title: 'Vehicle Accessories',
        manual: 'Manual',
        spareTire: 'Spare Tire',
        key: 'Key',
        keyQuantity: 'Key Quantity',
        toolKit: 'Tool Kit',
        firstAidKit: 'First Aid Kit',
        fireExtinguisher: 'Fire Extinguisher',
        warningTriangle: 'Warning Triangle',
        others: 'Others'
      },

      // Abnormal Conditions
      abnormal: {
        title: 'Abnormal Conditions',
        hasAbnormal: 'Has Abnormal',
        abnormalDescription: 'Abnormal Description',
        damageLevel: 'Damage Level',
        repairRequired: 'Repair Required',
        repairCost: 'Repair Cost',
        responsibleParty: 'Responsible Party',
        remarks: 'Remarks'
      },

      // Abnormal Photos
      photos: {
        title: 'Abnormal Photos',
        noPhotos: 'No Photos',
        uploadDate: 'Upload Date',
        photoDescription: 'Photo Description'
      },

      // Status Values
      yes: 'Yes',
      no: 'No',
      normal: 'Normal',
      abnormalStatus: 'Abnormal'
    },

    // Messages
    messages: {
      querySuccess: 'Query successful',
      queryFailed: 'Query failed',
      loadDetailFailed: 'Failed to load details'
    },

    // Empty State
    empty: {
      description: 'Please enter filter criteria and click [Search]'
    }
  },

  // Order Management Page
  management: {
    // Filter
    filterTitle: 'Order Filter',

    // Tab labels
    tabs: {
      sea: 'Sea Freight',
      air: 'Air Freight',
      land: 'Land Freight',
      rail: 'Rail Freight'
    },

    // Table Columns
    columns: {
      orderNo: 'Order Number',
      orderType: 'Order Type',
      pol: 'Port of Loading',
      pod: 'Port of Discharge',
      carrier: 'Carrier',
      vesselName: 'Vessel/Voyage',
      createdBy: 'Created By',
      dateCreated: 'Created Time',
      syncStatus: 'Sync Status',
      cargoJobNo: 'Cargo Job No.',
      customReferenceNo: 'Reference No.',
      mblNo: 'MBL No.',
      hblNo: 'HBL No.',
      containerNos: 'Container Nos.',
      actions: 'Actions'
    },

    // Modals
    modal: {
      createOrder: {
        title: 'Create Order',
        cancel: 'Cancel',
        confirm: 'Confirm'
      },
      syncOrder: {
        title: 'Sync Order',
        selectPlatform: 'Select Third-party Platform',
        selectPlatformPlaceholder: 'Please select third-party platform',
        selectPlatformRequired: 'Select Third-party Platform',
        selectModes: 'Select Sync Mode',
        selectModesRequired: 'Select Sync Mode',
        syncNewOrders: 'Sync New Orders',
        updateExistingOrders: 'Update Existing Orders',
        confirmSync: 'Confirm Sync',
        close: 'Close',
        successTitle: '🎉 Sync successful!',
        newOrders: 'New Orders:',
        updateOrders: 'Updated Orders:',
        tickets: 'orders'
      }
    },

    // Expand/Collapse
    expand: {
      expandAll: 'Expand',
      collapseAll: 'Collapse',
      expandAllTitle: 'Expand All',
      collapseAllTitle: 'Collapse All'
    },

    // Export
    export: {
      filename: 'Order Data',
      title: 'Order Management Table',
      contextMenuExportHint: 'Right-click on the table to export Excel / CSV files'
    }
  },

  // Booking page
  booking: {
    // Page title
    title: 'FCL Sea Freight Booking',
    pageTitle: {
      newBooking: 'New Booking Application',
      newOrder: 'New Order',
      priceNo: 'Rate No.: {priceNo}'
    },

    specialPrice: {
      title: 'Special Price Order',
      applicationNo: 'Application No.',
      rateType: 'Rate Type',
      expireAt: 'Valid Until',
      fcl: 'FCL',
      lcl: 'LCL',
      prePort: 'Pre-port',
      lastMile: 'Last-mile',
      lclQuantityTitle: 'Special Price Quantities',
      loadFailed: 'Failed to Load Special Price Application',
      loadFailedDescription: 'Unable to load the approved order data. Please try again later.',
      retry: 'Retry',
      back: 'Back',
      fclQuantityRequired: 'Select at least one approved container type',
      lclQuantityRequired: 'The billing quantity must be greater than 0',
      columns: {
        feeName: 'Charge Name',
        billingUnit: 'Billing Unit',
        approvedUnitPrice: 'Approved Unit Price',
        quantity: 'Quantity',
        subtotal: 'Subtotal'
      }
    },

    // Buttons
    buttons: {
      saveDraft: 'Save Draft',
      bookNow: 'Book Now',
      confirm: 'Confirm',
      cancel: 'Cancel'
    },

    // Tags
    tags: {
      viewMode: 'View Mode'
    },

    // Component titles
    componentTitles: {
      selectContainer: 'Select Container Type & Quantity',
      uploadBookingDoc: 'Upload Booking Document *',
      uploadOtherFiles: 'Upload Other Files',
      rateDetail: 'Rate Details'
    },

    // Route info section (RouteInfo)
    routeInfo: {
      contractPrice: 'Contract Rate',
      spotPrice: 'SPOT Rate',
      transit: 'Transit',
      direct: 'Direct',
      transitPort: 'Transit Port',
      allTransitPorts: 'All transit ports:',
      voyageDays: 'Voyage • {days} days',
      validPeriod: 'Validity: {from} ~ {to}',
      totalEstimatedCost: 'Estimated Total Cost',
      convertedAmount: '≈ CNY {amount}',
      seaFreightAndSurcharge: 'Including sea freight {seaFreight}{surcharge}',
      freightAndSurcharge: 'Including {freight} {freightAmount}{surcharge}',
      surchargeLabel: ' Surcharge {surcharge}',
      viewDetail: 'View Details',
      exchangeRateTip: '1 USD = 7.1419 CNY (updated at 2025-07-28 09:26:03). The exchange rate is for reference only, please refer to the actual bank settlement rate. For FCL quotations, ocean freight, surcharges and value-added services may be adjusted according to the actual situation of the booking, subject to final confirmation.',
      laneCode: 'Route Code',
      detentionFreeDays: 'Free Detention',
      demurrageFreeDays: 'Free Demurrage',
      spaceStatus: 'Space Status',
      overweightRemarks: 'Overweight Remarks',
      priceNotes: 'Rate Notes',
      expand: 'Expand',
      collapse: 'Collapse',
      loading: 'Loading...',
      daysUnit: 'Days'
    },

    // Schedule Selection
    schedule: {
      title: 'Shipping Schedule',
      selectSchedule: 'Select Schedule',
      changeSchedule: 'Change Schedule',
      notSelectedTip: 'No schedule selected, please click to select',
      vesselVoyage: 'Vessel / Voyage',
      etd: 'ETD',
      eta: 'ETA',
      customsCutoff: 'Customs Cutoff',
      portCutoff: 'Port Cutoff',
      declarationDeadline: 'Declaration Deadline',
      scheduleRequired: 'Please select a shipping schedule first',
      modalTitle: 'Select Shipping Schedule',
      emptyText: 'No matching schedule found',
      selectAction: 'Select',
      moreSchedules: 'More Schedules',
      confirmSelect: 'Successfully selected schedule'
    },

    // Rate detail drawer
    rateDetail: {
      containerMode: 'Charged by Container Type',
      nonContainerMode: 'Non-container Charges',
      seaFreight: 'Sea Freight',
      prePortFreight: 'Pre-port Freight',
      lastMileFreight: 'Last-mile Freight',
      surcharge: 'Surcharges',
      columns: {
        feeName: 'Charge Name',
        currency: 'Currency',
        measure: 'Billing Unit',
        price: 'Price',
        quantity: 'Quantity',
        subtotal: 'Subtotal',
        note: 'Remarks'
      }
    },

    // Tips
    tips: {
      uploadBookingDocTip: 'Uploading booking document will automatically trigger AI recognition to quickly help you fill in booking information'
    },

    // Modal
    modal: {
      confirmTitle: 'Confirm Submission',
      confirmMessage: 'Confirm to submit booking information?',
      confirmButton: 'Confirm',
      cancelButton: 'Cancel'
    },

    // Form validation messages
    validation: {
      placeOfDeliveryRequired: 'Place of delivery is required',
      polRequired: 'Port of loading is required',
      portOfDischargeRequired: 'Port of discharge is required',
      podRequired: 'Port of destination is required',
      piecesRequired: 'Pieces is required',
      weightRequired: 'Gross weight is required',
      cubageRequired: 'Volume is required',
      packagesRequired: 'Package type is required',
      termsTypeRequired: 'Terms type is required',
      releaseTypeRequired: 'Release type is required',
      englishGoodsDescriptionRequired: 'English goods description is required',
      shipperNameRequired: 'Shipper name is required',
      shipperAddressRequired: 'Shipper address is required',
      consigneeNameRequired: 'Consignee name is required',
      consigneeAddressRequired: 'Consignee address is required',
      cargoReadyTimeRangeRequired: 'Please select cargo ready time range',
      cargoReadyTimeDateRequired: 'Please select cargo ready date'
    },

    // Messages
    messages: {
      recognizing: 'Recognizing booking document content...',
      recognitionSuccess: 'Booking document recognition completed',
      recognitionFailed: 'Booking document recognition failed, please fill in the form manually',
      pleaseCompleteRequiredFields: 'Please complete required fields',
      bookingSuccess: 'Booking application submitted successfully',
      bookingFailed: 'Booking application failed',
      saveDraftSuccess: 'Draft saved successfully',
      cannotSaveDraft: 'Cannot save draft: task information not found',
      saveDraftRemarks: 'Save booking draft data',
      bookingTaskCompleted: 'Booking task completed',
      bookingSubmitted: 'Booking application submitted',
      getPriceDetailFailed: 'Failed to get price details',
      getQuotationDetailFailed: 'Failed to get quotation details',
      getInquiryDetailFailed: 'Failed to get inquiry details',
      getTaskDataFailed: 'Failed to get task data'
    },

    // Cargo Info Component
    cargoInfo: {
      warning: 'The following information needs to be filled in accurately, please fill in carefully. If there are any changes, please contact customer service for record-keeping in a timely manner',
      labels: {
        placeOfDelivery: 'Place of Delivery',
        pol: 'Port of Loading',
        portOfDischarge: 'Port of Discharge',
        pod: 'Port of Destination',
        vessel: 'Vessel Name',
        voyage: 'Voyage Number',
        pieces: 'No.of (Pieces)',
        weight: 'Gross Weight',
        cubage: 'Measurement (Volume)',
        packages: 'Packages',
        termsType: 'Terms Type',
        cargoReadyTime: 'Cargo Ready Time',
        releaseType: 'Type of Release',
        englishGoodsDescription: 'English Description of Goods',
        marksAndNumbers: 'Marks & Numbers',
        carrier: 'Carrier',
        cargoType: 'Cargo Type',
        transportTerms: 'Transport Terms',
        freightTerm: 'Freight Term'
      },
      tips: {
        generalOnly: 'Currently only general cargo booking is supported'
      },
      placeholders: {
        placeOfDelivery: 'Please select place of delivery',
        pol: 'Please select port of loading',
        portOfDischarge: 'Please select port of discharge',
        pod: 'Please select port of destination',
        vessel: 'Please enter vessel name (optional)',
        voyage: 'Please enter voyage number (optional)',
        pieces: 'Please enter',
        weight: 'Please enter',
        cubage: 'Please enter',
        packages: 'Please select package type',
        termsType: 'Please select trade terms',
        releaseType: 'Please select',
        englishGoodsDescription: 'Please enter',
        marksAndNumbers: 'Please enter',
        carrier: 'Please select carrier',
        cargoType: 'Please select cargo type',
        transportTerms: 'Please select transport terms',
        freightTerm: 'Please select freight term',
        cargoReadyTimeRange: 'Please select',
        cargoReadyTimeDate: 'Please select specific date'
      },
      cargoReadyTime: {
        range: 'Range',
        date: 'Date',
        withinWeek: 'Within a week',
        options: {
          threeDays: 'Within 3 days',
          oneWeek: 'Within a week',
          twoWeeks: 'Within two weeks',
          oneMonth: 'Within a month',
          moreThanMonth: 'More than a month',
          unknown: 'Time unknown'
        }
      },
      units: {
        weight: 'KG',
        volume: 'm³'
      }
    },

    // Contact Info Component
    contactInfo: {
      title: 'Contact Information',
      labels: {
        shipperName: 'Shipper (Name)',
        shipperAddress: 'Shipper\'s Address',
        consigneeName: 'Consignee (Name)',
        consigneeAddress: 'Consignee\'s Address',
        notifierName: 'Notifier (Name)',
        notifierAddress: 'Notify\'s Address',
        contractingParty: 'Contracting Party',
        carrierContactPlace: 'Carrier Contact Place',
        blIssuePlace: 'Place of B/L Issue',
        forwarderName: 'Freight Forwarder Enterprise Name',
        shipperContact: 'Shipper Contact Name',
        landline: 'Landline Phone'
      },
      placeholders: {
        shipperName: 'Please enter shipper name',
        shipperAddress: 'Please enter shipper address',
        consigneeName: 'Please enter consignee name',
        consigneeAddress: 'Please enter consignee address',
        notifierName: 'Please enter notifier name',
        notifierAddress: 'Please enter notifier address',
        contractingParty: 'Please enter contracting party',
        carrierContactPlace: 'Please select carrier contact place',
        blIssuePlace: 'Please select place of B/L issue',
        forwarderName: 'Please enter freight forwarder enterprise name',
        shipperContact: 'Please enter shipper contact name',
        countryCode: 'Country Code',
        areaCode: 'Area Code',
        phoneNumber: 'Phone Number'
      },
      checkboxes: {
        toOrder: 'TO ORDER',
        sameAsConsignee: 'SAME AS CONSIGNEE'
      }
    },

    // Container Selection Component
    containerSelection: {
      cargoType: 'Cargo Type',
      vesselVoyage: 'Vessel/Voyage',
      change: 'Change',
      unitPrice: 'Unit Price',
      containerTypeQuantity: 'Container Type & Quantity',
      addContainerType: 'Add Container Type',
      containerType: 'Container Type',
      quantity: 'Quantity',
      remove: 'Remove',
      modal: {
        confirmTitle: 'Confirm Change',
        confirmButton: 'Confirm',
        cancelButton: 'Cancel',
        confirmMessage: 'Changing container type and quantity may cause rate changes. Please confirm with customer service. Confirm to change?'
      }
    },

    // Order Remarks Component
    orderRemarks: {
      title: 'Order Remarks',
      placeholder: 'Please fill in order remarks here if you have special requirements'
    },

    // File Upload Component
    fileUpload: {
      defaultTitle: 'Upload File',
      defaultTip: 'Click or drag file to this area to upload',
      defaultUploadText: 'Click or drag file to this area to upload',
      defaultSubText: 'Up to 10 files can be uploaded',
      uploadLimitReached: 'Upload limit reached',
      uploadLimitText: 'Up to {limit} files can be uploaded, currently {current} uploaded',
      uploadLimitWarning: 'Only {limit} files can be uploaded, please delete existing files first',
      uploadLimitSize: 'Maximum file size is {maxSize}MB',
      fileNotExists: 'File object does not exist',
      uploadFailed: 'Upload failed, please try again',
      selectFailed: 'Selection failed, please try again',
      uploadAction: 'upload',
      selectAction: 'select',
      fileUploadFailed: 'File {fileName} {action} failed: {errorMessage}'
    }
  }
}
